import { create } from 'zustand';
import { toast } from 'sonner';
import { connectionsApi } from '@/lib/api/connections';
import { chatApi } from '@/lib/api/chat';
import { handleChatError } from '@/lib/chat/chatErrorHandler';
import {
  notifyTyping,
  subscribeToConversation,
  syncActiveConversationMessageListeners,
  unsubscribeFromConversation,
} from '@/lib/chat/chatRealtime';
import type {
  ChatConversation,
  ChatMessage,
  ChatMessageAttachmentInput,
  ChatPresence,
  ConnectionSearchUser,
  FriendRequest,
  MemberConnection,
} from '@/types/api';

export type ChatHubView = 'inbox' | 'friends' | 'requests' | 'discover';

export type IncomingMessageAlert = {
  conversationId: string;
  senderName: string;
  senderAvatar: string | null;
  preview: string;
};

export type FriendRequestAlert =
  | { type: 'received'; requestId: number; userId: string; userName: string; userAvatar: string | null }
  | { type: 'accepted'; requestId: number; userId: string; userName: string; userAvatar: string | null };

export type ChatInitScope = 'chat' | 'friends';

export interface ChatStoreState {
  initialized: boolean;
  initializedFriendsGraph: boolean;
  loading: boolean;
  actionLoading: boolean;
  myUserId: string | null;
  conversations: ChatConversation[];
  messagesByConversation: Record<string, ChatMessage[]>;
  messagePagination: Record<string, { page: number; hasMore: boolean }>;
  friends: MemberConnection[];
  sentRequests: FriendRequest[];
  receivedRequests: FriendRequest[];
  discoverPeople: ConnectionSearchUser[];
  discoverTotal: number;
  pendingDiscoverRequestIds: string[];
  presenceByUserId: Record<string, ChatPresence>;
  typingByConversation: Record<string, string | null>;
  activeConversationId: string | null;
  replyToMessage: ChatMessage | null;
  errorHandlerOptions: {
    onUnauthorized?: () => void;
    onEmailNotVerified?: () => void;
    onProfileIncomplete?: () => void;
  };
  incomingMessageAlert: IncomingMessageAlert | null;
  incomingMessageAlertSeq: number;
  friendRequestAlert: FriendRequestAlert | null;
  friendRequestAlertSeq: number;

  unreadTotal: () => number;
  pendingRequestsCount: () => number;

  initialize: (
    myUserId: string,
    errorOptions?: ChatStoreState['errorHandlerOptions'],
    scope?: ChatInitScope
  ) => Promise<void>;
  teardown: () => void;
  refreshInbox: () => Promise<void>;
  refreshFriends: () => Promise<void>;
  refreshRequests: () => Promise<void>;
  refreshReceivedRequests: () => Promise<void>;
  refreshSentRequests: () => Promise<void>;
  fetchDiscover: (filters?: {
    name?: string;
    gender?: string;
    minAge?: number;
    maxAge?: number;
    page?: number;
  }) => Promise<void>;
  openConversation: (conversationId: string) => Promise<void>;
  closeConversation: () => void;
  markConversationRead: (conversationId: string, options?: { silent?: boolean }) => Promise<void>;
  createConversationWithFriend: (friendId: string) => Promise<string | null>;
  loadMoreMessages: (conversationId: string) => Promise<void>;
  sendMessage: (conversationId: string, body: string, attachments?: ChatMessageAttachmentInput[]) => Promise<void>;
  editMessage: (conversationId: string, messageId: string, body: string) => Promise<void>;
  deleteMessage: (conversationId: string, messageId: string) => Promise<void>;
  toggleReaction: (conversationId: string, messageId: string, emoji: string) => Promise<void>;
  removeReaction: (conversationId: string, messageId: string) => Promise<void>;
  setReplyTo: (message: ChatMessage | null) => void;
  sendFriendRequest: (userId: string) => Promise<void>;
  acceptRequest: (id: number) => Promise<string | null>;
  declineRequest: (id: number) => Promise<void>;
  cancelRequest: (id: number) => Promise<void>;
  unfriend: (userId: string) => Promise<void>;
  refreshPresence: (userIds: string[]) => Promise<void>;
  notifyComposerFocus: (conversationId: string) => void;
  notifyComposerBlur: (conversationId: string) => void;
  notifyIncomingMessage: (alert: IncomingMessageAlert) => void;
  notifyFriendRequestAlert: (alert: FriendRequestAlert) => void;

  applyIncomingMessage: (conversationId: string, message: ChatMessage) => void;
  applyMessageStatuses: (conversationId: string | null, messageIds: string[], status: ChatMessage['status']) => void;
  applyMessagePatch: (conversationId: string, messageId: string, patch: Partial<ChatMessage>) => void;
  applyReactionAdded: (conversationId: string, messageId: string, userId: string, emoji: string) => void;
  applyReactionRemoved: (conversationId: string, messageId: string, userId: string) => void;
  setConversationTyping: (conversationId: string, userId: string | null) => void;
}

function sortConversations(list: ChatConversation[]): ChatConversation[] {
  return [...list].sort((a, b) => (b.lastMessageAt ?? '').localeCompare(a.lastMessageAt ?? ''));
}

/** API responses (e.g. delete) may omit `reactions`; keep store messages safe for UI. */
function normalizeChatMessage(message: ChatMessage, existing?: ChatMessage): ChatMessage {
  return {
    ...message,
    reactions: message.reactions ?? existing?.reactions ?? [],
    attachments: message.attachments ?? existing?.attachments ?? [],
  };
}

function normalizeChatMessages(messages: ChatMessage[]): ChatMessage[] {
  return messages.map((m) => normalizeChatMessage(m));
}

function shouldSuppressIncomingMessageNotification(conversationId: string): boolean {
  if (typeof window === 'undefined') return false;
  if (window.location.pathname !== '/member/chat') return false;
  return useChatStore.getState().activeConversationId === conversationId;
}

export const useChatStore = create<ChatStoreState>((set, get) => ({
  initialized: false,
  initializedFriendsGraph: false,
  loading: false,
  actionLoading: false,
  myUserId: null,
  conversations: [],
  messagesByConversation: {},
  messagePagination: {},
  friends: [],
  sentRequests: [],
  receivedRequests: [],
  discoverPeople: [],
  discoverTotal: 0,
  pendingDiscoverRequestIds: [],
  presenceByUserId: {},
  typingByConversation: {},
  activeConversationId: null,
  replyToMessage: null,
  errorHandlerOptions: {},
  incomingMessageAlert: null,
  incomingMessageAlertSeq: 0,
  friendRequestAlert: null,
  friendRequestAlertSeq: 0,

  unreadTotal: () => get().conversations.reduce((sum, c) => sum + (c.unreadCount ?? 0), 0),

  pendingRequestsCount: () =>
    get().receivedRequests.filter((r) => r.status === 'pending').length,

  initialize: async (myUserId, errorOptions, scope = 'chat') => {
    const sameUser = get().myUserId === myUserId;
    if (scope === 'friends') {
      if (sameUser && get().initializedFriendsGraph) return;
    } else if (sameUser && get().initialized) {
      return;
    }
    set({
      myUserId,
      loading: true,
      errorHandlerOptions: errorOptions ?? {},
    });
    try {
      if (scope === 'friends') {
        // Friends tab's sub-nav pills need only the friends list + pending
        // received count for their badges. Sent requests, discover/search,
        // the "message a friend" inbox lookup, and the full contacts list
        // are each fetched lazily by their own sub-tab (see UserProfile.tsx).
        await Promise.all([get().refreshFriends(), get().refreshReceivedRequests()]);
        set({ initializedFriendsGraph: true });
      } else {
        // Chat tab only needs the inbox and the received-requests count
        // (for the friend-request badge) — not friends/sent/discover.
        await Promise.all([get().refreshInbox(), get().refreshReceivedRequests()]);
        set({ initialized: true });
      }
    } catch (err) {
      handleChatError(err, errorOptions);
    } finally {
      set({ loading: false });
    }
  },

  teardown: () => {
    unsubscribeFromConversation();
    set({
      initialized: false,
      initializedFriendsGraph: false,
      activeConversationId: null,
      conversations: [],
      messagesByConversation: {},
      replyToMessage: null,
    });
  },

  refreshInbox: async () => {
    const conversations = await chatApi.listConversations();
    const userIds = conversations.map((c) => c.participant.id);
    if (userIds.length) await get().refreshPresence(userIds);
    set({ conversations: sortConversations(conversations) });
  },

  refreshFriends: async () => {
    const friends = await connectionsApi.listFriends();
    set({ friends });
  },

  refreshRequests: async () => {
    const [sentRequests, receivedRequests] = await Promise.all([
      connectionsApi.getSentRequests(),
      connectionsApi.getReceivedRequests(),
    ]);
    set({ sentRequests, receivedRequests });
  },

  refreshReceivedRequests: async () => {
    const receivedRequests = await connectionsApi.getReceivedRequests();
    set({ receivedRequests });
  },

  refreshSentRequests: async () => {
    const sentRequests = await connectionsApi.getSentRequests();
    set({ sentRequests });
  },

  fetchDiscover: async (filters = {}) => {
    set({ loading: true });
    try {
      const { data, total } = await chatApi.discoverPeople({
        page: filters.page ?? 1,
        limit: 20,
        name: filters.name,
        gender: filters.gender,
        minAge: filters.minAge,
        maxAge: filters.maxAge,
      });
      set({ discoverPeople: data, discoverTotal: total });
    } catch (err) {
      handleChatError(err, get().errorHandlerOptions);
    } finally {
      set({ loading: false });
    }
  },

  openConversation: async (conversationId) => {
    set({ activeConversationId: conversationId, loading: true, replyToMessage: null });
    subscribeToConversation(conversationId, () => get());
    try {
      const { data, pagination } = await chatApi.getMessages(conversationId, 1, 30);
      set((s) => ({
        messagesByConversation: {
          ...s.messagesByConversation,
          [conversationId]: normalizeChatMessages(data),
        },
        messagePagination: {
          ...s.messagePagination,
          [conversationId]: { page: 1, hasMore: (pagination.nextPage ?? 0) > 1 },
        },
      }));
      await get().markConversationRead(conversationId);
      syncActiveConversationMessageListeners(() => get());
    } catch (err) {
      handleChatError(err, get().errorHandlerOptions);
    } finally {
      set({ loading: false });
    }
  },

  closeConversation: () => {
    unsubscribeFromConversation();
    set({ activeConversationId: null, replyToMessage: null });
  },

  markConversationRead: async (conversationId, options) => {
    try {
      await chatApi.markRead(conversationId);
      set((s) => ({
        conversations: s.conversations.map((c) =>
          c.id === conversationId ? { ...c, unreadCount: 0 } : c
        ),
      }));
      await get().refreshInbox();
    } catch (err) {
      if (!options?.silent) {
        handleChatError(err, get().errorHandlerOptions);
      }
    }
  },

  createConversationWithFriend: async (friendId) => {
    set({ actionLoading: true });
    try {
      const conv = await chatApi.createConversation(friendId);
      await get().refreshInbox();
      return conv.id;
    } catch (err) {
      handleChatError(err, get().errorHandlerOptions);
      return null;
    } finally {
      set({ actionLoading: false });
    }
  },

  loadMoreMessages: async (conversationId) => {
    const pag = get().messagePagination[conversationId];
    if (!pag?.hasMore) return;
    const nextPage = pag.page + 1;
    try {
      const { data, pagination } = await chatApi.getMessages(conversationId, nextPage, 30);
      set((s) => ({
        messagesByConversation: {
          ...s.messagesByConversation,
          [conversationId]: [
            ...(s.messagesByConversation[conversationId] ?? []),
            ...normalizeChatMessages(data),
          ],
        },
        messagePagination: {
          ...s.messagePagination,
          [conversationId]: { page: nextPage, hasMore: (pagination.nextPage ?? 0) > nextPage },
        },
      }));
      syncActiveConversationMessageListeners(() => get());
    } catch (err) {
      handleChatError(err, get().errorHandlerOptions);
    }
  },

  sendMessage: async (conversationId, body, attachments) => {
    const reply = get().replyToMessage;
    try {
      const message = await chatApi.sendMessage(conversationId, body, reply?.id ?? null, attachments);
      set((s) => ({
        replyToMessage: null,
        messagesByConversation: {
          ...s.messagesByConversation,
          [conversationId]: [
            normalizeChatMessage(message),
            ...(s.messagesByConversation[conversationId] ?? []),
          ],
        },
      }));
      syncActiveConversationMessageListeners(() => get());
      await get().refreshInbox();
    } catch (err) {
      handleChatError(err, get().errorHandlerOptions);
    }
  },

  editMessage: async (conversationId, messageId, body) => {
    try {
      const updated = await chatApi.editMessage(conversationId, messageId, body);
      set((s) => ({
        messagesByConversation: {
          ...s.messagesByConversation,
          [conversationId]: (s.messagesByConversation[conversationId] ?? []).map((m) =>
            m.id === messageId ? normalizeChatMessage({ ...m, ...updated }) : m
          ),
        },
      }));
    } catch (err) {
      handleChatError(err, get().errorHandlerOptions);
    }
  },

  deleteMessage: async (conversationId, messageId) => {
    try {
      const updated = await chatApi.deleteMessage(conversationId, messageId);
      set((s) => ({
        messagesByConversation: {
          ...s.messagesByConversation,
          [conversationId]: (s.messagesByConversation[conversationId] ?? []).map((m) =>
            m.id === messageId ? normalizeChatMessage({ ...m, ...updated }) : m
          ),
        },
      }));
    } catch (err) {
      handleChatError(err, get().errorHandlerOptions);
    }
  },

  toggleReaction: async (conversationId, messageId, emoji) => {
    const myId = get().myUserId;
    const msg = get().messagesByConversation[conversationId]?.find((m) => m.id === messageId);
    const has = msg?.reactions?.some((r) => r.userId === myId && r.emoji === emoji);
    try {
      if (has) await chatApi.removeReaction(conversationId, messageId);
      else await chatApi.addReaction(conversationId, messageId, emoji);
      const { data } = await chatApi.getMessages(conversationId, 1, 50);
      set((s) => ({
        messagesByConversation: {
          ...s.messagesByConversation,
          [conversationId]: normalizeChatMessages(data),
        },
      }));
    } catch (err) {
      handleChatError(err, get().errorHandlerOptions);
    }
  },

  removeReaction: async (conversationId, messageId) => {
    try {
      await chatApi.removeReaction(conversationId, messageId);
      const { data } = await chatApi.getMessages(conversationId, 1, 50);
      set((s) => ({
        messagesByConversation: {
          ...s.messagesByConversation,
          [conversationId]: normalizeChatMessages(data),
        },
      }));
    } catch (err) {
      handleChatError(err, get().errorHandlerOptions);
    }
  },

  setReplyTo: (message) => set({ replyToMessage: message }),

  sendFriendRequest: async (userId) => {
    set((s) => ({
      actionLoading: true,
      pendingDiscoverRequestIds: s.pendingDiscoverRequestIds.includes(userId)
        ? s.pendingDiscoverRequestIds
        : [...s.pendingDiscoverRequestIds, userId],
    }));
    try {
      const result = await connectionsApi.sendRequest(userId);
      await get().refreshRequests();
      set((s) => ({
        discoverPeople: s.discoverPeople.filter((p) => p.id !== userId),
        discoverTotal: Math.max(0, s.discoverTotal - 1),
        pendingDiscoverRequestIds: s.pendingDiscoverRequestIds.filter((id) => id !== userId),
      }));
      toast.success(result.message);
    } catch (err) {
      set((s) => ({
        pendingDiscoverRequestIds: s.pendingDiscoverRequestIds.filter((id) => id !== userId),
      }));
      handleChatError(err, get().errorHandlerOptions);
    } finally {
      set({ actionLoading: false });
    }
  },

  acceptRequest: async (id) => {
    const request = get().receivedRequests.find((r) => r.id === id);
    const friendId = request?.requesterId ?? null;
    set({ actionLoading: true });
    try {
      await connectionsApi.acceptRequest(id);
      await Promise.all([get().refreshRequests(), get().refreshFriends(), get().refreshInbox()]);
      return friendId;
    } catch (err) {
      handleChatError(err, get().errorHandlerOptions);
      return null;
    } finally {
      set({ actionLoading: false });
    }
  },

  declineRequest: async (id) => {
    set({ actionLoading: true });
    try {
      await connectionsApi.declineRequest(id);
      await get().refreshRequests();
    } catch (err) {
      handleChatError(err, get().errorHandlerOptions);
    } finally {
      set({ actionLoading: false });
    }
  },

  cancelRequest: async (id) => {
    set({ actionLoading: true });
    try {
      await connectionsApi.cancelRequest(id);
      await get().refreshRequests();
    } catch (err) {
      handleChatError(err, get().errorHandlerOptions);
    } finally {
      set({ actionLoading: false });
    }
  },

  unfriend: async (userId) => {
    set({ actionLoading: true });
    try {
      await connectionsApi.unfriend(userId);
      await get().refreshFriends();
    } catch (err) {
      handleChatError(err, get().errorHandlerOptions);
    } finally {
      set({ actionLoading: false });
    }
  },

  refreshPresence: async (userIds) => {
    try {
      const data = await chatApi.getPresence(userIds);
      set((s) => {
        const presenceByUserId = { ...s.presenceByUserId };
        for (const p of data) presenceByUserId[p.userId] = p;
        return { presenceByUserId };
      });
    } catch {
      /* optional */
    }
  },

  notifyComposerFocus: (conversationId) => {
    notifyTyping(conversationId, true);
  },

  notifyComposerBlur: (conversationId) => {
    notifyTyping(conversationId, false);
  },

  notifyIncomingMessage: (alert) => {
    if (shouldSuppressIncomingMessageNotification(alert.conversationId)) return;
    set((s) => ({
      incomingMessageAlert: alert,
      incomingMessageAlertSeq: s.incomingMessageAlertSeq + 1,
    }));
  },

  notifyFriendRequestAlert: (alert) => {
    set((s) => ({
      friendRequestAlert: alert,
      friendRequestAlertSeq: s.friendRequestAlertSeq + 1,
    }));
  },

  applyIncomingMessage: (conversationId, message) => {
    set((s) => {
      const existing = s.messagesByConversation[conversationId] ?? [];
      const idx = existing.findIndex((m) => m.id === message.id);
      const next =
        idx >= 0
          ? existing.map((m) =>
              m.id === message.id ? normalizeChatMessage(message, m) : m
            )
          : [normalizeChatMessage(message), ...existing];
      return {
        messagesByConversation: { ...s.messagesByConversation, [conversationId]: next },
      };
    });
  },

  applyMessageStatuses: (conversationId, messageIds, status) => {
    set((s) => {
      const convIds = conversationId
        ? [conversationId]
        : Object.keys(s.messagesByConversation);
      const messagesByConversation = { ...s.messagesByConversation };
      for (const cid of convIds) {
        const list = messagesByConversation[cid];
        if (!list) continue;
        messagesByConversation[cid] = list.map((m) =>
          messageIds.includes(m.id) ? { ...m, status } : m
        );
      }
      return { messagesByConversation };
    });
  },

  applyMessagePatch: (conversationId, messageId, patch) => {
    set((s) => ({
      messagesByConversation: {
        ...s.messagesByConversation,
        [conversationId]: (s.messagesByConversation[conversationId] ?? []).map((m) =>
          m.id === messageId ? { ...m, ...patch } : m
        ),
      },
    }));
  },

  applyReactionAdded: (conversationId, messageId, userId, emoji) => {
    set((s) => ({
      messagesByConversation: {
        ...s.messagesByConversation,
        [conversationId]: (s.messagesByConversation[conversationId] ?? []).map((m) => {
          if (m.id !== messageId) return m;
          const others = (m.reactions ?? []).filter((r) => r.userId !== userId);
          return { ...m, reactions: [...others, { userId, emoji }] };
        }),
      },
    }));
  },

  applyReactionRemoved: (conversationId, messageId, userId) => {
    set((s) => ({
      messagesByConversation: {
        ...s.messagesByConversation,
        [conversationId]: (s.messagesByConversation[conversationId] ?? []).map((m) => {
          if (m.id !== messageId) return m;
          return { ...m, reactions: (m.reactions ?? []).filter((r) => r.userId !== userId) };
        }),
      },
    }));
  },

  setConversationTyping: (conversationId, userId) => {
    set((s) => ({
      typingByConversation: { ...s.typingByConversation, [conversationId]: userId },
    }));
  },
}));
